import React, { use, useCallback, useEffect, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import PreIdVerificationImage from "../../assets/images/ill.svg";
import ScreenCard, { ButtonType } from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { KycStatus, ProfileType } from "../../__generated__/graphql";
import { UserFieldsFragment } from "../../__generated__/operations/graphql";
import MarkdownView from "../../components/MarkdownView";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { kycGradient } from "../../configs/linear-gradients";
import { useTracking } from "../../hooks/tracking";
import {
    AuthManagerContext,
    IS_INITIAL_WORKER_ONBOARDING,
    IS_POST_ONBOARDING_PREPARE_DISPLAYED,
} from "../../contexts/AuthManager";
import Loading from "../../components/Loading";
import ScreenHeader from "../user/ScreenHeader";
import { useIntercom } from "../../hooks/intercom";
import { useTheme } from "../../hooks/use-theme";
import { useDeleteCurrentProfile } from "../../hooks/use-delete-current-profile";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useQuery } from "@apollo/client/react";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { ButtonMode } from "../../components/Button";
import { safeAsyncStorage } from "../../utils/safe-async-storage";
import { useDeleteAccount } from "../../hooks/use-delete-account";

const getI18nKeyForOnboardingPermitType = (
    user: UserFieldsFragment,
    profileType?: ProfileType,
): string => {
    if (profileType === ProfileType.Client) {
        return "screens.auth.preIdVerification.description_client";
    }
    if (!user.workerProfile?.swissOrHasPermitAnswer) {
        return "screens.auth.preIdVerification.description_no_permit";
    } else {
        return "screens.auth.preIdVerification.description_swiss_or_permit";
    }
};

type PreIdVerificationProps = StackScreenProps<RootStackParamList, "PreIdVerification">;

const PreIdVerification = ({ navigation, route }: PreIdVerificationProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const insets = useSafeAreaInsets();
    const { profileType, profileId, profileIds, openProfileSwitch } = use(
        ProfileSwitcherManagerContext,
    );
    const currentProfile = useCurrentProfile();
    const { user } = use(AuthManagerContext);
    const isLarge = useIsScreenSizeMin("large");
    const { trackEvent } = useTracking();
    const onDeleteAccount = useDeleteAccount();
    const { presentMessageComposer } = useIntercom();
    const { data: getClientUsers } = useQuery(GET_CLIENT_USERS, {
        variables: { clientId: currentProfile?.client?.id || "" },
        skip: profileType !== ProfileType.Client,
    });
    const { backOnSkip = false } = route.params || {};
    const next = useCallback(() => {
        navigation.navigate("VeriffScreen");
    }, [navigation]);

    const skip = useCallback(() => {
        if (backOnSkip) {
            navigation.goBack();
            return;
        }
        navigation.reset({ index: 0, routes: [{ name: "Home" }] });
    }, [backOnSkip, navigation]);

    const isPrivateClient = currentProfile?.isPrivateClient || false;

    const mandatory = useMemo(() => {
        if (profileType === ProfileType.Worker) {
            // for user that onboard with a worker profile KYC is mandatory
            return true;
        } else if (profileType === ProfileType.Client) {
            if (
                !isPrivateClient &&
                (getClientUsers?.getClientUsers.length || 0) > 1 &&
                user?.kycStatus?.status !== KycStatus.Accepted
            ) {
                return true;
            }
        }
        return false;
    }, [
        profileType,
        isPrivateClient,
        getClientUsers?.getClientUsers.length,
        user?.kycStatus?.status,
    ]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("screens.auth.preIdVerification.nextButton"),
                onPress: next,
            },
            ...(!mandatory
                ? [
                      {
                          title: i18n.t("screens.auth.preIdVerification.skipButton"),
                          onPress: skip,
                      },
                  ]
                : [
                      {
                          title: i18n.t("profile.change_profile"),
                          onPress: openProfileSwitch,
                          mode: "secondary" as ButtonMode,
                      },
                  ]),
        ];
    }, [i18n, mandatory, next, openProfileSwitch, skip]);

    useEffect(() => {
        void trackEvent("kyc_showed");
    }, [trackEvent]);

    const deleteCurrentProfile = useDeleteCurrentProfile({
        profileType: profileType || ProfileType.Worker,
        profileId: `${profileType}:${profileId}`,
        isPrivateClient: currentProfile?.isPrivateClient || false,
    });

    return (
        <ScreenCard backgroundGradient={kycGradient} buttons={buttons}>
            <ScreenHeader
                useSmallLabel
                leftIcon="__none__"
                backgroundColor="transparent"
                leftActionDisabled
                onLeftAction={() => {}}
                label={i18n.t("screens.auth.preIdVerification.title")}
                actions={
                    !backOnSkip
                        ? [
                              {
                                  label: i18n.t("assistance"),
                                  onPress: () => {
                                      void presentMessageComposer();
                                  },
                                  icon: "assistance",
                              },
                              // If only 1 profile (worker), delete the account
                              ...(profileIds.length === 1
                                  ? [
                                        {
                                            label: i18n.t(`profile.delete_account_title`),
                                            onPress: () => {
                                                void safeAsyncStorage.removeItem(
                                                    IS_POST_ONBOARDING_PREPARE_DISPLAYED,
                                                );
                                                void safeAsyncStorage.removeItem(
                                                    IS_INITIAL_WORKER_ONBOARDING,
                                                );
                                                onDeleteAccount();
                                            },
                                            color: colors.error,
                                            icon: "trash" as const,
                                        },
                                    ]
                                  : // If has multiple profiles, delete the current profile
                                    [
                                        {
                                            label: i18n.t(`profile.delete.${profileType}.profile`),
                                            onPress: () => {
                                                void deleteCurrentProfile();
                                            },
                                            color: colors.error,
                                            icon: "trash" as const,
                                        },
                                    ]),
                          ]
                        : []
                }
            />
            {!user && <Loading />}
            {user && (
                <VStack
                    gap={1}
                    h="2/3"
                    v={1.5}
                    style={{
                        paddingTop: insets.top,
                        marginBottom: 0,
                        paddingBottom: 0,
                    }}
                >
                    <PreIdVerificationImage width="100%" style={{ overflow: "visible" }} />
                    <MarkdownView
                        markdown={i18n.t(getI18nKeyForOnboardingPermitType(user, profileType))}
                        markdownStyle={
                            profileType === ProfileType.Client
                                ? undefined
                                : { textgroup: { textAlign: "center" } }
                        }
                        style={isLarge ? { alignItems: "center" } : undefined}
                    />
                </VStack>
            )}
        </ScreenCard>
    );
};

export default PreIdVerification;
